import { useState, useEffect, useCallback } from 'react';
import type { DisplayOutputManager } from '../managers/DisplayOutputManager';

export type DisplayOutputHookResult = {
  displays: { displayId: number; width: number; height: number; x: number; y: number }[];
  selectedDisplayId: number;
  isLive: boolean;
  elapsedTime: number;
  onDisplayClick: (index: number) => void;
};

export function useDisplayOutput(manager: DisplayOutputManager): DisplayOutputHookResult {
  const store = manager.getStore();
  const [state, setState] = useState(store.currentState);
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    setState(store.currentState);
    const id = store.addListener((s) => setState(s));
    return () => store.removeListener(id);
  }, [store]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (state.startTime === 0) {
        setElapsedTime(0);
      } else {
        setElapsedTime(Date.now() - state.startTime);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [state.startTime]);

  const onDisplayClick = useCallback(
    (index: number) => {
      const display = state.displays[index];
      if (display) {
        manager.changeDisplay(display.displayId);
      }
    },
    [manager, state.displays],
  );

  return {
    displays: state.displays,
    selectedDisplayId: state.selectedDisplayId,
    isLive: state.isLive,
    elapsedTime,
    onDisplayClick,
  };
}
